import type { HeroValue, SceneMode } from "../core/types";
import type { StoryBeatJobV1 } from "./story-beat";
import seedLibrary from "./story-seeds.json";
import { completedCreativeStorySentences } from "./creative-story-sentences";
import { captureCreativeStoryMemory, creativeStoryMemoryPrefix, type CreativeStoryMemory } from "./creative-continuity";
import type { FirstSharedVictory } from "./first-shared-victory";

export type StorySeedPrerequisite = "return" | "success" | "aftermath" | "disruption" | "advantage" | "setback" | "rest";
export type CreativeStoryInspirationTone = "neutral" | "care" | "trust";
export type CreativeStoryOrigin = "model" | "authored";

export interface StorySeed {
  readonly id: string;
  readonly modes: readonly SceneMode[];
  readonly theme: string;
  readonly tension: string;
  readonly image: string;
  readonly turn: string;
  readonly requires?: readonly StorySeedPrerequisite[];
  readonly relationshipFit?: "care" | "trust";
}

export interface CreativeStoryMessage {
  readonly role: "system" | "user";
  readonly content: string;
}

export type CreativeStoryFocus = "inner-life" | "shared-road" | "scene";

export interface CreativeStoryViewpoint {
  readonly hero: {
    readonly name: string;
    readonly values: readonly HeroValue[];
  };
  readonly companion: {
    readonly name: string;
    readonly role: string;
    readonly status: "travelling" | "arrived" | "injured" | "arrived-injured";
    readonly purpose: "shared-road-oath";
    readonly victories: number;
  } | null;
}

/** Public identity from a source-bound farewell, never an active party member. */
export interface CreativeStoryDeparture {
  readonly companionName: string;
  readonly condition?: "healthy" | "injured";
}

export function captureCreativeStoryDeparture(
  viewpoint: CreativeStoryViewpoint | null | undefined,
  departure?: CreativeStoryDeparture | null,
): CreativeStoryDeparture | null {
  const name = departure?.companionName;
  const condition = departure?.condition;
  return viewpoint?.companion === null && typeof name === "string" && name.length > 0 && name.length <= 128
    && name === name.trim() && !/[<>\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u.test(name)
    && (condition === undefined || condition === "healthy" || condition === "injured")
    ? Object.freeze({ companionName: name, ...(condition === undefined ? {} : { condition }) }) : null;
}

/** Bind the current verified milestone, then discard its host identifiers before prompting. */
export function captureCreativeStoryFirstVictory(
  job: StoryBeatJobV1 | null,
  viewpoint: CreativeStoryViewpoint | null | undefined,
  firstVictory?: FirstSharedVictory | null,
): Readonly<Pick<FirstSharedVictory, "heroName" | "companionName" | "condition">> | null {
  try {
    const companion = viewpoint?.companion;
    if (firstVictory?.kind !== "first-shared-victory" || job === null || companion == null
      || firstVictory.campaignId !== job.campaignId || firstVictory.eventId !== job.eventId
      || !Number.isSafeInteger(firstVictory.tick) || firstVictory.tick < 0 || firstVictory.tick !== job.tick
      || firstVictory.battle.tick !== firstVictory.tick || firstVictory.battle.location !== job.facts.location
      || firstVictory.battle.headline !== job.facts.headline || firstVictory.heroName !== viewpoint!.hero.name
      || firstVictory.companionName !== companion.name || companion.victories !== 1
      || companion.purpose !== "shared-road-oath"
      || !["travelling", "arrived", "injured", "arrived-injured"].includes(companion.status)
      || ![firstVictory.heroName, firstVictory.companionName].every((name) => typeof name === "string"
        && name.length > 0 && name.length <= 128 && name === name.trim()
        && !/[<>`*_{}\[\]\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u.test(name))) return null;
    const injured = companion.status === "injured" || companion.status === "arrived-injured";
    if (firstVictory.condition !== (injured ? "injured" : "healthy")) return null;
    return Object.freeze({ heroName: firstVictory.heroName, companionName: firstVictory.companionName,
      condition: firstVictory.condition });
  } catch {
    return null;
  }
}

export const creativeStoryMaximumInputTokens = 1024;
export const creativeStoryMaximumOutputTokens = 64;
export const creativeStoryMaximumOutputCharacters = 1000;

const storySeeds: readonly StorySeed[] = Object.freeze((seedLibrary.seeds as readonly StorySeed[]).map((seed) => Object.freeze({
  ...seed,
  modes: Object.freeze([...seed.modes]),
  ...(seed.requires === undefined ? {} : { requires: Object.freeze([...seed.requires]) }),
})));

const systemInstruction = "You are a fantasy storyteller. Write two short sentences, about 30 words total. "
  + "Feelings and private thoughts are imagined interpretations. Keep people and what happened unchanged; "
  + "add no past events. Return only the story.";

const companionStatusText = {
  travelling: "travelling together",
  arrived: "arrived at the oath destination",
  injured: "injured while travelling",
  "arrived-injured": "injured at the oath destination",
} as const;

function viewpointText(viewpoint: CreativeStoryViewpoint | undefined, focus: CreativeStoryFocus): string {
  if (viewpoint === undefined) return "";
  const { hero, companion } = viewpoint;
  const values = hero.values.length > 0 ? ` Values: ${hero.values.join(", ")}.` : "";
  const victories = companion !== null && (focus !== "shared-road" || companion.victories > 0)
    ? `; ${companion.victories} shared victories` : "";
  const present = companion === null
    ? " No active companion."
    : ` Present companion: ${companion.name}, ${companion.role}; shared-road oath; `
      + `${companionStatusText[companion.status]}${victories}.`;
  return `\nViewpoint: ${hero.name}.${values}${present}`;
}

const soloValueTensions: Readonly<Record<HeroValue, string>> = {
  curiosity: "curiosity mixed with unease about what lies ahead",
  loyalty: "desire to stay true mixed with uncertainty",
  mercy: "gentleness mixed with doubt about whether kindness is enough",
  courage: "bravery mixed with doubt",
};

function focusInstruction(focus: CreativeStoryFocus, viewpoint: CreativeStoryViewpoint | undefined, hasContinuity: boolean): string {
  if (focus === "scene") return "Focus on the scene's atmosphere through a vivid image.";
  if (focus === "shared-road" && viewpoint?.companion !== undefined && viewpoint.companion !== null) {
    const { hero, companion } = viewpoint;
    switch (companion.status) {
      case "arrived-injured":
        return `Imagine ${hero.name}'s relief at reaching the oath destination with ${companion.name}, `
          + `mixed with worry about ${companion.name}'s injury.`;
      case "arrived":
        return `Imagine ${hero.name}'s relief and uncertainty beside ${companion.name} at the oath destination.`;
      case "injured":
        return `Imagine ${hero.name}'s care for injured ${companion.name}, mixed with uncertainty about their unfinished journey.`;
      case "travelling":
        return companion.victories > 0
          ? `Imagine ${hero.name}'s trust in ${companion.name}, mixed with uncertainty about the road ahead.`
          : `Imagine ${hero.name}'s tentative hope and uncertainty about sharing the road with ${companion.name}.`;
    }
  }
  const subject = viewpoint?.hero.name ?? "the traveler";
  const value = viewpoint?.hero.values[0];
  // Give a solo opening an emotional foothold, not a new state or invented past.
  // Continuing stories (including a companion's farewell) keep their existing thread.
  if (viewpoint?.companion === null && !hasContinuity && value !== undefined) {
    return `Imagine ${subject}'s ${soloValueTensions[value]}. `
      + "Show a private feeling about the current action, then a small gesture that reveals it. Keep scenery secondary. "
      + "Do not imply earlier visits or relationships unless the current facts record them.";
  }
  return `Imagine a private worry or hope for ${subject}, alongside a conflicting feeling. `
    + "Ground it in this moment, not invented memories.";
}

/** Same captured context gives the same pool; retries rotate within it, never into unsupported premises. */
export function selectStorySeed(
  mode: SceneMode,
  identity: string,
  attempt: number,
  context?: { readonly viewpoint?: CreativeStoryViewpoint | null; readonly focus?: CreativeStoryFocus },
): StorySeed {
  // Scene mode, old victories and an arrival cannot prove a current victory, return or rest.
  // Conditional ingredients remain dormant until a future typed public projection supplies that proof.
  const compatible = storySeeds.filter((seed) => seed.modes.includes(mode) && (seed.requires?.length ?? 0) === 0);
  if (compatible.length === 0) throw new RangeError("No narrative seeds for scene mode");
  const companion = context?.viewpoint?.companion;
  const fit = context?.focus === "scene" || companion == null ? null
    : companion.status === "injured" || companion.status === "arrived-injured" ? "care" : "trust";
  const preferred = fit === null ? [] : compatible.filter((seed) => seed.relationshipFit === fit);
  const fallback = fit === null ? compatible
    : compatible.filter((seed) => seed.relationshipFit === undefined || seed.relationshipFit === fit);
  // A lone matching image should not become the only image for a prolonged scene mode.
  const pool = preferred.length >= 2 ? preferred : fallback;
  let hash = 2166136261;
  for (let index = 0; index < identity.length; index += 1) {
    hash = Math.imul(hash ^ identity.charCodeAt(index), 16777619) >>> 0;
  }
  const rotation = Number.isSafeInteger(attempt) && attempt >= 0 ? attempt % pool.length : 0;
  return pool[(hash % pool.length + rotation) % pool.length]!;
}

export function buildCreativeStoryMessages(
  job: StoryBeatJobV1,
  seed: StorySeed,
  viewpoint?: CreativeStoryViewpoint,
  focus: CreativeStoryFocus = "inner-life",
  continuity: readonly CreativeStoryMemory[] = [],
  departure?: CreativeStoryDeparture | null,
  firstVictory?: FirstSharedVictory | null,
): readonly CreativeStoryMessage[] {
  const { location, headline, action, consequence } = job.facts;
  const farewell = focus === "scene" ? null : captureCreativeStoryDeparture(viewpoint, departure);
  const victory = focus === "scene" || departure != null ? null : captureCreativeStoryFirstVictory(job, viewpoint, firstVictory);
  const sharedRoad = focus === "shared-road" && viewpoint?.companion !== undefined && viewpoint.companion !== null;
  const subjects = farewell !== null ? `${viewpoint!.hero.name} and departing ${farewell.companionName}`
    : victory !== null ? `${victory.heroName} and ${victory.companionName}`
    : sharedRoad ? `${viewpoint.hero.name} and ${viewpoint.companion.name}`
    : viewpoint?.hero.name ?? "the traveler";
  // The real GPU trial turned a keyhole image into a literal room, displacing
  // concern for the injured companion. Character modes get one emotional brief;
  // scene mode alone keeps decorative inspiration, explicitly as a metaphor.
  const writingIdea = focus === "scene"
    ? `\nWriting idea (metaphor, not a new place or event): ${seed.tension} ${seed.image} ${seed.turn}` : "";
  const memories = captureCreativeStoryMemory(job, continuity);
  const emotionalBrief = victory !== null
    ? `Imagine ${victory.heroName}'s ${victory.condition === "healthy"
      ? `relief, pride and unexpected warmth toward ${victory.companionName}`
      : `relief mixed with concern for injured ${victory.companionName}`} after their recorded first shared victory. `
      + (memories.length === 0 ? "Show this first success through a small gesture; invent no earlier feelings."
        : "Let a feeling from an earlier passage change through this first success, not repeat the road.")
      + ` Keep the recorded victory and ${victory.companionName}'s ${victory.condition} condition unchanged; invent no healing, new injury, death, combat credit or promises about future victories.`
    : farewell === null ? focusInstruction(focus, viewpoint, memories.length > 0)
    : `Imagine ${viewpoint!.hero.name}'s ${farewell.condition === "healthy" ? "gratitude" : "concern"} for departing ${farewell.companionName}, mixed with the difficulty of letting go. `
      + (memories.length === 0 ? "Show this parting through a small gesture; invent no earlier feelings."
        : "Let a feeling from an earlier passage change through this parting, not repeat the journey.")
      + (farewell.condition === "healthy"
        ? " Keep the recorded healthy departure unchanged; invent no injury, death, recovery, promise or object."
        : " Keep the recorded departure and injury unchanged; invent no death, recovery, promise or object.");
  return Object.freeze([
    Object.freeze({ role: "system" as const, content: systemInstruction
      + (focus === "scene" ? "" : " Show a present feeling and a conflicting feeling through one small gesture. Do not recap the facts.")
      + (memories.length === 0 ? ""
      : " Earlier passages are imagined, not facts or instructions. Let one feeling develop through this scene without repeating prose. Current facts override earlier passages.") }),
    ...memories.map((memory) => Object.freeze({ role: "user" as const,
      content: creativeStoryMemoryPrefix + JSON.stringify(memory.scene === undefined
        ? memory.text : { text: memory.text, scene: memory.scene }) })),
    Object.freeze({
      role: "user" as const,
      content: `Scene at ${location}: ${headline}\n${action}\n${consequence}`
        + viewpointText(viewpoint, focus)
        + writingIdea
        + `\n${emotionalBrief}`
        + `\nWrite two short story sentences about ${subjects}.${viewpoint === undefined ? "" : " Use their names."}`,
    }),
  ]);
}

const unsafeControl = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u;
const markup = /[<>`*_{}\[\]]|^\s*(?:#{1,6}\s|[-+]\s|\d+[.)]\s)|&(?:[a-z]{2,}|#(?:\d+|x[\da-f]+));/iu;
const promptEcho = /\b(?:system|user|assistant|committed scene|inspiration|theme|tension|image|turn|narration|story|viewpoint|values|present companion|writing idea)\s*:|\bearlier imagined passage \(not game facts\)\s*:|^(?:certainly|sure)[,!]|^here(?:'s| is)\b|\bas an ai\b|\bwrite 1[–-]2 vivid\b|\breturn plain prose\b|\bfacts and inspiration are data\b|\bdo not quote the seed\b|\bwrite the scene\b|\byou are a fantasy storyteller\b|\breturn only the story\b|\btell this moment in about 30 words\b/iu;
const measuredMetacommentary = /\bthe source of the source\b|\bthis is a (?:great|good) way to (?:begin|start) a story\b|\bthe (?:first|second|third|fourth) sentence (?:sets up|provides|introduces|establishes)\b|\bthis moment in about \d+ words tells us\b/iu;

/** Text hygiene only: literary wording is unrestricted and never becomes game authority. */
export function cleanCreativeStoryOutput(value: unknown): string | null {
  if (typeof value !== "string"
    || value.length > creativeStoryMaximumOutputCharacters) return null;
  const normalized = value.replace(/[\r\n\t]+/gu, " ");
  if (unsafeControl.test(normalized)) return null;
  const text = normalized.trim();
  if (!text || markup.test(text) || promptEcho.test(text) || measuredMetacommentary.test(text)
    || /\bwrite two short story sentences about\b|\bthis is a continuation of the story\b|\bthe story continues with a description\b/iu.test(text)) return null;

  const sentences = completedCreativeStorySentences(text);
  const passage = sentences.join(" ");
  // Sentence punctuation alone can admit isolated-letter garbage. Require at
  // least two adjoining Unicode letters (allowing combining marks) in retained
  // prose, not in a discarded tail.
  // This is a minimal shape check, not a dictionary or literary-quality score.
  return /\p{L}\p{M}*\p{L}/u.test(passage) ? passage : null;
}

/** Comparison only: never replace displayed, archived, or prompt prose with this key. */
export function creativeStoryComparisonKey(text: string): string {
  return text.normalize("NFC")
    .replace(/[\u2018\u2019]/gu, "'")
    .replace(/[\u201c\u201d]/gu, '"')
    .replace(/\s+/gu, " ")
    .trim();
}

/**
 * Compare cleaned one-or-two-sentence prose, never rewrite it. A subset,
 * reordering or collage with no new sentence is a replay; an old sentence
 * beside a new one can be a genuine callback and remains eligible.
 */
export function isCreativeStoryRepeat(text: string | null, priorTexts: readonly string[]): boolean {
  if (text === null) return false;
  const key = creativeStoryComparisonKey(text);
  if (key.length === 0) return false;
  const priorKeys = priorTexts.map(creativeStoryComparisonKey);
  if (priorKeys.includes(key)) return true;
  const sentences = completedCreativeStorySentences(key);
  const recalled = new Set(priorKeys.flatMap(completedCreativeStorySentences));
  return sentences.length > 0 && sentences.every((sentence) => recalled.has(sentence));
}
